import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'
import useWebSocketTaxonomy from './useWebSocketTaxonomy'
import { ConnectionState } from '@/lib/websocket-manager'

// ── Types ────────────────────────────────────────────────────────────
export interface TaxonomyRun {
  run_id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number                  // 0–100
  total_chunks_processed: number
  intents_discovered: number
  error_log: string
  created_at: string
  completed_at: string | null
  // WebSocket-specific fields for real-time updates
  step?: string                     // Current pipeline step name
  chunks_in_step?: number           // Chunks processed in current step
}

export interface TaxonomyIntent {
  id: string
  name: string
  description: string
  document_count: number
  cluster_id: number
  approved: 'pending' | 'approved' | 'rejected'
  tags: { id: string; name: string; description: string; approved: string }[]
  entity_types: { id: string; name: string; description: string; example: string; approved: string }[]
}

export interface TaxonomyResult {
  run_id: string
  status: string
  intents_discovered: number
  intents: TaxonomyIntent[]
}

export interface GraphData {
  nodes: { name: string; description: string; example: string; intent_name: string; approved: string }[]
  edges: { source: string; target: string; weight: number }[]
}

// ── Mock data (used when DEV_MODE_OVERRIDE is set) ────────────────────
const MOCK_RUN: TaxonomyRun = {
  run_id: 'mock-run-0000-0000-000000000001',
  status: 'completed',
  progress: 100,
  total_chunks_processed: 68,
  intents_discovered: 6,
  error_log: '',
  created_at: new Date(Date.now() - 120000).toISOString(),
  completed_at: new Date().toISOString(),
}

const MOCK_RUNNING_RUN: TaxonomyRun = {
  ...MOCK_RUN,
  status: 'running',
  progress: 42,
  completed_at: null,
}

const MOCK_FAILED_RUN: TaxonomyRun = {
  ...MOCK_RUN,
  status: 'failed',
  progress: 35,
  error_log: 'Step: embed\ntraceback: ValueError — model not found',
  completed_at: new Date().toISOString(),
}

const MOCK_RESULT: TaxonomyResult = {
  run_id: MOCK_RUN.run_id,
  status: 'completed',
  intents_discovered: 3,
  intents: [
    {
      id: 'mock-intent-1',
      name: 'Park Maintenance',
      description: 'Requests related to park upkeep and facilities.',
      document_count: 24,
      cluster_id: 0,
      approved: 'pending',
      tags: [
        { id: 'mock-tag-1', name: 'maintenance', description: 'General upkeep tasks', approved: 'pending' },
        { id: 'mock-tag-2', name: 'facilities', description: 'Physical infrastructure', approved: 'pending' },
      ],
      entity_types: [
        { id: 'mock-et-1', name: 'PARK_NAME', description: 'Name of the park', example: 'Lincoln Park', approved: 'pending' },
        { id: 'mock-et-2', name: 'TRAIL_NAME', description: 'Name of a trail', example: 'Oak Ridge Trail', approved: 'pending' },
      ],
    },
    {
      id: 'mock-intent-2',
      name: 'Wildlife Management',
      description: 'Issues involving wildlife sightings and management.',
      document_count: 18,
      cluster_id: 1,
      approved: 'pending',
      tags: [
        { id: 'mock-tag-3', name: 'wildlife', description: 'Animals and habitat', approved: 'pending' },
      ],
      entity_types: [
        { id: 'mock-et-3', name: 'SPECIES_NAME', description: 'Animal species', example: 'Red-tailed Hawk', approved: 'pending' },
      ],
    },
    {
      id: 'mock-intent-3',
      name: 'Visitor Services',
      description: 'Visitor information and amenity requests.',
      document_count: 12,
      cluster_id: 2,
      approved: 'pending',
      tags: [
        { id: 'mock-tag-4', name: 'visitor', description: 'Guest-facing services', approved: 'pending' },
        { id: 'mock-tag-5', name: 'amenities', description: 'Facilities for visitors', approved: 'pending' },
      ],
      entity_types: [
        { id: 'mock-et-4', name: 'FACILITY_NAME', description: 'Name of a facility', example: 'Visitor Center', approved: 'pending' },
      ],
    },
  ],
}

const MOCK_GRAPH: GraphData = {
  nodes: [
    { name: 'PARK_NAME',     description: 'Name of the park',     example: 'Lincoln Park',      intent_name: 'Park Maintenance',   approved: 'pending' },
    { name: 'TRAIL_NAME',    description: 'Name of a trail',      example: 'Oak Ridge Trail',   intent_name: 'Park Maintenance',   approved: 'pending' },
    { name: 'SPECIES_NAME',  description: 'Animal species',       example: 'Red-tailed Hawk',   intent_name: 'Wildlife Management', approved: 'pending' },
    { name: 'FACILITY_NAME', description: 'Name of a facility',   example: 'Visitor Center',    intent_name: 'Visitor Services',   approved: 'pending' },
  ],
  edges: [
    { source: 'PARK_NAME',    target: 'TRAIL_NAME',    weight: 1.0  },
    { source: 'PARK_NAME',    target: 'SPECIES_NAME',  weight: 0.6  },
    { source: 'PARK_NAME',    target: 'FACILITY_NAME', weight: 0.4  },
    { source: 'TRAIL_NAME',   target: 'SPECIES_NAME',  weight: 0.3  },
  ],
}

// ── Dev-mode override state ──────────────────────────────────────────
// Components set this. The hook reads it. Exported so GenClairTab can
// drive it via the state switcher dropdown.
export type DevOverride = 'empty' | 'running' | 'completed' | 'error' | null
let _devOverride: DevOverride = null
export function setDevOverride(v: DevOverride) { _devOverride = v }
export function getDevOverride(): DevOverride  { return _devOverride }

// ── Main hook ────────────────────────────────────────────────────────
export function useTaxonomyRun(kbId: string) {
  const queryClient = useQueryClient()
  const override = getDevOverride()
  const isDev = import.meta.env.DEV
  
  // Connect to WebSocket for real-time updates
  const { 
    runData: wsRunData,
    connectionState: wsConnectionState
  } = useWebSocketTaxonomy(kbId)

  // ── Initial fetch and occasional refresh of latest run ──
  const latestRun = useQuery<TaxonomyRun | null>({
    queryKey: ['taxonomy-latest', kbId],
    queryFn: async () => {
      // Dev override — return mock data without hitting the API
      if (isDev && override === 'empty')     return null
      if (isDev && override === 'running')   return MOCK_RUNNING_RUN
      if (isDev && override === 'completed') return MOCK_RUN
      if (isDev && override === 'error')     return MOCK_FAILED_RUN

      try {
        const res = await knowledgeApi.get(`/knowledge/${kbId}/taxonomy/latest/`)
        return res.data as TaxonomyRun
      } catch (err: any) {
        if (err?.response?.status === 404) {
          console.log('No taxonomy run found yet')
          return null   // no run yet — that's fine
        }
        throw err
      }
    },
    enabled: !!kbId,
    // Initial fetch when component mounts
    refetchOnMount: true,
    
    // Minimal polling as a fallback if WebSockets are not available
    // Only poll every 30 seconds to reduce load, since WebSockets are primary data source
    refetchInterval: (query) => {
      // If WebSocket is connected, don't need to poll at all
      if (wsConnectionState === ConnectionState.CONNECTED && wsRunData) {
        return false;
      }
      
      // If no WebSocket connection, poll as a fallback
      const data = query.state.data;
      if (!data) return 30000;  // Once per 30 seconds if no data
      
      // For completed/failed state, no need to poll
      if (data.status === 'completed' || data.status === 'failed') return false;
      
      // For pending/running, poll every 30 seconds as backup
      return 30000;
    },
    
    // Make sure polling stays active when tab is in background
    refetchIntervalInBackground: true,
    
    // Use WebSocket data if available
    select: (data) => {
      // If we have WebSocket data and it's for the same run, use that instead
      // This keeps the UI updated in real-time without waiting for poll interval
      if (wsRunData && data?.run_id === wsRunData.run_id) {
        console.log('Using WebSocket data for taxonomy run');
        return wsRunData;
      }
      return data;
    }
  })

  // ── Fetch full result (only when completed) ──
  const fullResult = useQuery<TaxonomyResult>({
    queryKey: ['taxonomy-result', kbId, latestRun.data?.run_id],
    queryFn: async () => {
      if (isDev && override === 'completed') return MOCK_RESULT
      const res = await knowledgeApi.get(`/knowledge/${kbId}/taxonomy/runs/${latestRun.data!.run_id}/result/`)
      return res.data as TaxonomyResult
    },
    enabled: !!latestRun.data && latestRun.data.status === 'completed',
  })

  // ── Fetch graph (only when completed) ──
  const graphData = useQuery<GraphData>({
    queryKey: ['taxonomy-graph', kbId, latestRun.data?.run_id],
    queryFn: async () => {
      if (isDev && override === 'completed') return MOCK_GRAPH
      const res = await knowledgeApi.get(`/knowledge/${kbId}/taxonomy/runs/${latestRun.data!.run_id}/graph/`)
      return res.data as GraphData
    },
    enabled: !!latestRun.data && latestRun.data.status === 'completed',
  })

  // ── Trigger a new run ──
  const createRun = useMutation({
    mutationFn: async () => {
      const res = await knowledgeApi.post(`/knowledge/${kbId}/taxonomy/runs/`)
      return res.data as TaxonomyRun
    },
    onSuccess: (newRun) => {
      // Immediately update the cache with the new run so UI switches right away
      queryClient.setQueryData(['taxonomy-latest', kbId], newRun)
      // Also invalidate to ensure fresh data on next fetch
      queryClient.invalidateQueries({ queryKey: ['taxonomy-latest', kbId] })
    },
  })

  // ── Delete a run ──
  const deleteRun = useMutation({
    mutationFn: async (runId: string) => {
      return knowledgeApi.delete(`/knowledge/${kbId}/taxonomy/runs/${runId}/delete/`)
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['taxonomy-latest', kbId] })
    },
  })

  return { latestRun, fullResult, graphData, createRun, deleteRun }
}
